import { type RefObject, useLayoutEffect } from 'react'
import { MOTION, prefersReducedMotion } from '@/lib/motion'

/**
 * The hand-off from the brand loader to the page it stood in for: the loader
 * fades OUT on top while the page fades IN underneath, so there is never a hard
 * cut and never a blank frame between them (ui-motion-vocabulary.feature,
 * "The loader hands over to the page").
 *
 * ── Why a ghost and not a kept-mounted loader ──────────────────────────────
 * Both loaders that hand over leave the tree the instant the page is ready: the
 * session loader is swapped for the page by RequireAuth, and the in-shell one
 * is a Suspense fallback React drops on its own. Holding either back would hold
 * the page back too. So the loader leaves on time, and a detached, inert COPY
 * of it — the ghost — stays on top at the same place and fades away, then
 * removes itself (docs/ui-effects.md §11 rule 6: a detached one-shot element
 * self-removes on `animationend`). The page is there, and clickable, from the
 * first frame: the ghost takes no pointer events and is hidden from assistive
 * tech.
 *
 * ── Reduced motion ─────────────────────────────────────────────────────────
 * No ghost and no reveal: the swap is instant, as a wizard step is.
 */

/** How long a ghost or a reveal may outlive its animation before it is dropped anyway. */
export const HANDOFF_FAILSAFE_MS = MOTION.state + 120

// ── The very first paint ────────────────────────────────────────────────────
//
// A loader fades IN when it appears — except the one on the app's first paint,
// which is simply there: fading it in from the empty page would add a blank
// frame at boot rather than remove one. App marks the first commit as painted.

let firstPaint = true

/** Whether the app has not yet committed its first frame. */
export function isFirstPaint(): boolean {
  return firstPaint
}

/** Called once the app's first frame is on screen (App's mount effect). */
export function markFirstPaint(): void {
  firstPaint = false
}

/** Tests only: back to a cold boot. */
export function resetFirstPaint(): void {
  firstPaint = true
}

// ── The ghost ───────────────────────────────────────────────────────────────

/**
 * True for the rest of the commit in which a loader handed over, so content
 * mounting in that same commit knows to fade in. Reset in a microtask: a page
 * that mounts later did not replace a loader.
 */
let handingOver = false

function onAnimationDone(el: HTMLElement, done: () => void): () => void {
  const onEnd = (e: AnimationEvent) => {
    if (e.target === el) finish()
  }
  const failsafe = window.setTimeout(() => finish(), HANDOFF_FAILSAFE_MS)
  function finish() {
    window.clearTimeout(failsafe)
    el.removeEventListener('animationend', onEnd)
    done()
  }
  el.addEventListener('animationend', onEnd)
  return finish
}

/**
 * Leave a fading copy of `el` where it stands. Called while React is removing
 * it (a layout-effect cleanup, when the node is still in the document).
 */
function spawnLoaderGhost(el: HTMLElement): void {
  if (prefersReducedMotion()) return
  handingOver = true
  const rect = el.getBoundingClientRect()
  const ghost = el.cloneNode(true) as HTMLElement
  // The band keeps its place in its sweep instead of restarting: each animated
  // layer of the copy is delayed back to where the original had got to.
  const from = [el, ...el.querySelectorAll<HTMLElement>('*')]
  const to = [ghost, ...ghost.querySelectorAll<HTMLElement>('*')]
  from.forEach((node, i) => {
    const band = node
      .getAnimations?.()
      .find((a) => (a as CSSAnimation).animationName?.startsWith('shimmer'))
    const t = band?.currentTime
    if (typeof t === 'number' && to[i]) to[i].style.animationDelay = `-${t}ms`
  })
  ghost.removeAttribute('role')
  ghost.setAttribute('aria-hidden', 'true')
  ghost.setAttribute('inert', '')
  ghost.dataset.slot = 'brand-loader-ghost'
  ghost.dataset.loaderMotion = 'exit'
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    zIndex: '60',
  })
  queueMicrotask(() => {
    handingOver = false
    // StrictMode's rehearsal unmount leaves the node in place: no hand-off happened.
    if (el.isConnected) return
    document.body.appendChild(ghost)
    onAnimationDone(ghost, () => ghost.remove())
  })
}

/** A loader that hands over to the page when it leaves (see spawnLoaderGhost). */
export function useLoaderHandoff(ref: RefObject<HTMLElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    const el = ref.current
    if (!enabled || !el) return
    return () => spawnLoaderGhost(el)
  }, [ref, enabled])
}

/**
 * Content that fades in when it takes over from a loader in the same commit.
 * Anything else — a navigation, a re-render — mounts without it.
 */
export function useRevealAfterLoader(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !handingOver) return
    el.dataset.loaderReveal = ''
    const finish = onAnimationDone(el, () => {
      delete el.dataset.loaderReveal
    })
    return finish
  }, [ref])
}
